import { useState } from "react"

import { AxiosError, AxiosResponse } from "axios"
import { UseQueryResult } from "react-query"
import {
  emptySelection,
  getBanner,
  getHeaderState,
  getSelectedCount,
  isAllLoadedSelected,
  isRowChecked,
  selectAllMatching,
  Selection,
  toggleHeader,
  toggleRow,
  toMatchingIdsAnswer,
} from "utils/transactionSelection"

interface IUseTransactionSelectionArgs {
  // Loaded rows the user may tick (all of them, or all but the non-deletable).
  selectableIds: number[]
  // The view's "/ids" query for its applied filters, enabled on demand.
  useMatchingIds: (
    enabled: boolean
  ) => UseQueryResult<AxiosResponse<number[]>, AxiosError>
}

/**
 * Selection state for a transaction list: row and header checkboxes, the
 * "/ids" count once every loaded row is ticked, and the select-all-matching
 * banner. The rules live in `utils/transactionSelection`.
 */
export const useTransactionSelection = ({
  selectableIds,
  useMatchingIds,
}: IUseTransactionSelectionArgs) => {
  const [selection, setSelection] = useState<Selection>(emptySelection)
  const matchingIdsQuery = useMatchingIds(
    isAllLoadedSelected(selection, selectableIds)
  )
  const headerState = getHeaderState(selection, selectableIds)

  const clear = () => setSelection(emptySelection)

  return {
    selection,
    clear,
    isRowChecked: (id: number) => isRowChecked(selection, id),
    toggleRow: (id: number) => setSelection((prev) => toggleRow(prev, id)),
    header: {
      isChecked: headerState === "checked",
      isIndeterminate: headerState === "indeterminate",
      isDisabled: !selectableIds.length,
      onToggle: () =>
        setSelection((prev) => toggleHeader(prev, selectableIds)),
    },
    bar: {
      selectedCount: getSelectedCount(selection),
      banner: getBanner(
        selection,
        selectableIds,
        toMatchingIdsAnswer(matchingIdsQuery)
      ),
      onSelectAllMatching: () => {
        if (matchingIdsQuery.data) {
          setSelection(selectAllMatching(matchingIdsQuery.data.data))
        }
      },
      onClearSelection: clear,
      onRetry: () => {
        matchingIdsQuery.refetch()
      },
    },
  }
}
